import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, Send, Trash2, Loader2, Plus, ChevronDown, ChevronUp, MessagesSquare, ThumbsUp, Tag, Pin, PinOff } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const when = (d) => new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

const Avatar = ({ name, admin }) => (
  <div className={`w-9 h-9 rounded-full flex items-center justify-center font-display text-sm shrink-0 uppercase ${admin ? "bg-[#D90429] text-white" : "bg-slate-200 text-[#0A1128]"}`}>
    {(name || "?").charAt(0)}
  </div>
);

export default function Forum() {
  const [posts, setPosts] = useState([]);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState("General Discussion");
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState("All");
  const [posting, setPosting] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [comments, setComments] = useState({});
  const [text, setText] = useState("");
  const [commenting, setCommenting] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const loadPosts = (cat = activeCat) => {
    const q = cat && cat !== "All" ? `?category=${encodeURIComponent(cat)}` : "";
    return api.get(`/forum/posts${q}`).then(({ data }) => setPosts(data)).catch(() => {});
  };
  useEffect(() => {
    loadPosts("All");
    api.get("/forum/categories").then(({ data }) => setCategories(data)).catch(() => {});
  }, []);

  const selectCat = (c) => { setActiveCat(c); loadPosts(c); setOpenId(null); };

  const submitPost = async () => {
    if (!user) { toast.info("Please log in to post."); navigate("/login"); return; }
    if (!title.trim() || !body.trim()) { toast.error("Add a title and some content."); return; }
    setPosting(true);
    try {
      await api.post("/forum/posts", { title: title.trim(), body: body.trim(), category });
      setTitle(""); setBody(""); setCategory("General Discussion");
      toast.success("Posted to the forum!");
      selectCat("All");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create post");
    } finally { setPosting(false); }
  };

  const toggleLikePost = async (id) => {
    if (!user) { toast.info("Log in to react."); navigate("/login"); return; }
    try {
      const { data } = await api.post(`/forum/posts/${id}/like`);
      setPosts((ps) => ps.map((p) => p.id === id ? {
        ...p, likes: data.likes,
        liked_by: data.liked ? [...(p.liked_by || []), user.email] : (p.liked_by || []).filter((e) => e !== user.email),
      } : p));
    } catch { toast.error("Could not react"); }
  };

  const toggleLikeComment = async (postId, cid) => {
    if (!user) { toast.info("Log in to react."); navigate("/login"); return; }
    try {
      const { data } = await api.post(`/forum/comments/${cid}/like`);
      setComments((c) => ({
        ...c,
        [postId]: (c[postId] || []).map((x) => x.id === cid ? {
          ...x, likes: data.likes,
          liked_by: data.liked ? [...(x.liked_by || []), user.email] : (x.liked_by || []).filter((e) => e !== user.email),
        } : x),
      }));
    } catch { toast.error("Could not react"); }
  };

  const toggleOpen = async (id) => {
    if (openId === id) { setOpenId(null); return; }
    setOpenId(id); setText("");
    if (!comments[id]) {
      try {
        const { data } = await api.get(`/forum/posts/${id}/comments`);
        setComments((c) => ({ ...c, [id]: data }));
      } catch { setComments((c) => ({ ...c, [id]: [] })); }
    }
  };

  const addComment = async (id) => {
    if (!user) { toast.info("Please log in to comment."); navigate("/login"); return; }
    if (!text.trim()) return;
    setCommenting(true);
    try {
      await api.post(`/forum/posts/${id}/comments`, { text: text.trim() });
      const { data } = await api.get(`/forum/posts/${id}/comments`);
      setComments((c) => ({ ...c, [id]: data }));
      setText("");
      setPosts((ps) => ps.map((p) => (p.id === id ? { ...p, comment_count: (p.comment_count || 0) + 1 } : p)));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not post comment");
    } finally { setCommenting(false); }
  };

  const togglePin = async (id) => {
    try {
      const { data } = await api.post(`/forum/posts/${id}/pin`);
      toast.success(data.pinned ? "Pinned to top" : "Unpinned");
      loadPosts();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not update pin"); }
  };

  const deletePost = async (id) => {
    try {
      await api.delete(`/forum/posts/${id}`);
      toast.success("Post deleted");
      setOpenId(null);
      loadPosts();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not delete"); }
  };

  const deleteComment = async (postId, cid) => {
    try {
      await api.delete(`/forum/comments/${cid}`);
      setComments((c) => ({ ...c, [postId]: (c[postId] || []).filter((x) => x.id !== cid) }));
      setPosts((ps) => ps.map((p) => (p.id === postId ? { ...p, comment_count: Math.max(0, (p.comment_count || 1) - 1) } : p)));
    } catch (err) { toast.error(err.response?.data?.detail || "Could not delete"); }
  };

  return (
    <div>
      <Navbar />

      <section className="relative pt-40 pb-20 grain overflow-hidden bg-[#0A1128]">
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8">
          <span className="text-sm uppercase tracking-[0.3em] font-semibold text-[#F4A261]">Members Community</span>
          <h1 className="font-display text-5xl sm:text-7xl uppercase text-white mt-4 leading-[0.92]">The <span className="text-[#D90429]">Forum</span></h1>
          <p className="text-white/80 text-lg max-w-2xl mt-6 leading-relaxed">Start a discussion, ask questions, and rally with fellow patriots. Log in as a member to post and comment.</p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          {/* Composer */}
          {user ? (
            <div className="bg-white border border-slate-200 p-6 mb-10" data-testid="forum-composer">
              <div className="flex items-center gap-2 mb-4">
                <Plus className="w-5 h-5 text-[#D90429]" />
                <h2 className="font-display text-xl uppercase">Start A Discussion</h2>
              </div>
              <select
                data-testid="forum-category-select"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full border border-slate-300 px-3 py-2.5 mb-3 bg-white focus:outline-none focus:border-[#D90429] transition-colors duration-200"
              >
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <input
                data-testid="forum-title-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Post title"
                className="w-full border border-slate-300 px-3 py-2.5 mb-3 focus:outline-none focus:border-[#D90429] transition-colors duration-200"
              />
              <textarea
                data-testid="forum-body-input"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="What's on your mind, patriot?"
                rows={4}
                className="w-full border border-slate-300 px-3 py-2.5 resize-none focus:outline-none focus:border-[#D90429] transition-colors duration-200"
              />
              <button
                data-testid="forum-post-submit"
                onClick={submitPost}
                disabled={posting}
                className="mt-3 inline-flex items-center gap-2 bg-[#D90429] text-white px-6 py-2.5 uppercase tracking-[0.15em] text-sm font-bold hover:bg-[#b0031f] transition-colors duration-200 disabled:opacity-60"
              >
                {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Post
              </button>
            </div>
          ) : (
            <div className="bg-slate-50 border border-slate-200 p-5 mb-10 text-sm text-slate-600" data-testid="forum-login-prompt">
              <Link to="/login" className="text-[#D90429] font-semibold hover:underline">Log in</Link> or{" "}
              <Link to="/signup" className="text-[#D90429] font-semibold hover:underline">create an account</Link> to start a discussion and comment.
            </div>
          )}

          {/* Category filter */}
          {categories.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-6" data-testid="forum-category-filter">
              {["All", ...categories].map((c) => (
                <button
                  key={c}
                  data-testid={`forum-filter-${c.replace(/\s+/g, "-").toLowerCase()}`}
                  onClick={() => selectCat(c)}
                  className={`px-4 py-2 text-xs uppercase tracking-[0.12em] font-bold border transition-colors duration-200 ${
                    activeCat === c ? "bg-[#0A1128] text-white border-[#0A1128]" : "bg-white text-slate-500 border-slate-200 hover:border-[#0A1128]"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          )}

          {/* Posts */}
          {posts.length === 0 ? (
            <div className="text-center py-16 text-slate-400">
              <MessagesSquare className="w-10 h-10 mx-auto mb-3 opacity-40" />
              No discussions yet. Be the first to post!
            </div>
          ) : (
            <div className="space-y-4">
              {posts.map((p) => {
                const canDeletePost = user && (user.role === "admin" || user.email === p.user_email);
                const open = openId === p.id;
                const list = comments[p.id];
                return (
                  <div key={p.id} data-testid="forum-post" className="bg-white border border-slate-200">
                    <div className="p-5">
                      <div className="flex gap-3">
                        <Avatar name={p.user_name} admin={p.is_admin} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-sm text-[#0A1128]">{p.user_name}</span>
                            {p.is_admin && <span className="text-[9px] uppercase tracking-wider bg-[#0A1128] text-white px-1.5 py-0.5">Team</span>}
                            <span className="text-xs text-slate-400">{when(p.created_at)}</span>
                            <div className="ml-auto flex items-center gap-2">
                              {user?.role === "admin" && (
                                <button
                                  data-testid={`forum-pin-${p.id}`}
                                  onClick={() => togglePin(p.id)}
                                  className={p.pinned ? "text-[#D90429]" : "text-slate-300 hover:text-[#0A1128]"}
                                  title={p.pinned ? "Unpin" : "Pin to top"}
                                >
                                  {p.pinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
                                </button>
                              )}
                              {canDeletePost && (
                                <button data-testid={`forum-post-delete-${p.id}`} onClick={() => deletePost(p.id)} className="text-slate-300 hover:text-[#D90429]" title="Delete post">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </div>
                          <h3 className="font-display text-xl uppercase text-[#0A1128] mt-1 leading-tight">{p.title}</h3>
                          <div className="flex items-center gap-2 flex-wrap mt-1.5">
                            {p.pinned && (
                              <span data-testid={`forum-pinned-badge-${p.id}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-white bg-[#0A1128] px-2 py-0.5">
                                <Pin className="w-3 h-3" /> Pinned
                              </span>
                            )}
                            {p.category && (
                              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-[#D90429] bg-[#D90429]/10 px-2 py-0.5">
                                <Tag className="w-3 h-3" /> {p.category}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-700 mt-1.5 whitespace-pre-wrap break-words">{p.body}</p>
                          <div className="mt-3 flex items-center gap-4">
                            <button
                              data-testid={`forum-like-post-${p.id}`}
                              onClick={() => toggleLikePost(p.id)}
                              className={`inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.12em] font-bold transition-colors duration-200 ${
                                user && (p.liked_by || []).includes(user.email) ? "text-[#D90429]" : "text-slate-500 hover:text-[#D90429]"
                              }`}
                            >
                              <ThumbsUp className="w-4 h-4" /> {p.likes || 0}
                            </button>
                            <button
                              data-testid={`forum-toggle-${p.id}`}
                              onClick={() => toggleOpen(p.id)}
                              className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.12em] font-bold text-slate-500 hover:text-[#0A1128] transition-colors duration-200"
                            >
                              <MessageSquare className="w-4 h-4" /> {p.comment_count || 0} {p.comment_count === 1 ? "Comment" : "Comments"}
                              {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <AnimatePresence>
                      {open && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden border-t border-slate-100 bg-slate-50"
                        >
                          <div className="p-5" data-testid={`forum-comments-${p.id}`}>
                            {user ? (
                              <div className="flex gap-3 mb-5">
                                <Avatar name={user.name || user.email} admin={user.role === "admin"} />
                                <div className="flex-1">
                                  <textarea
                                    data-testid="forum-comment-input"
                                    value={text}
                                    onChange={(e) => setText(e.target.value)}
                                    placeholder="Add a comment…"
                                    rows={2}
                                    className="w-full border border-slate-300 px-3 py-2 text-sm resize-none focus:outline-none focus:border-[#D90429]"
                                  />
                                  <button
                                    data-testid="forum-comment-submit"
                                    onClick={() => addComment(p.id)}
                                    disabled={commenting || !text.trim()}
                                    className="mt-2 inline-flex items-center gap-2 bg-[#D90429] text-white px-5 py-2 uppercase tracking-[0.15em] text-xs font-bold hover:bg-[#b0031f] transition-colors duration-200 disabled:opacity-50"
                                  >
                                    {commenting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Comment
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <p className="text-sm text-slate-500 mb-5"><Link to="/login" className="text-[#D90429] font-semibold hover:underline">Log in</Link> to join the conversation.</p>
                            )}

                            {!list ? (
                              <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></div>
                            ) : list.length === 0 ? (
                              <p className="text-slate-400 text-sm">No comments yet.</p>
                            ) : (
                              <div className="space-y-4">
                                {list.map((c) => {
                                  const canDel = user && (user.role === "admin" || user.email === c.user_email);
                                  return (
                                    <div key={c.id} data-testid="forum-comment" className="flex gap-3">
                                      <Avatar name={c.user_name} admin={c.is_admin} />
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                          <span className="font-semibold text-sm text-[#0A1128]">{c.user_name}</span>
                                          {c.is_admin && <span className="text-[9px] uppercase tracking-wider bg-[#0A1128] text-white px-1.5 py-0.5">Team</span>}
                                          <span className="text-xs text-slate-400">{when(c.created_at)}</span>
                                          {canDel && (
                                            <button data-testid={`forum-comment-delete-${c.id}`} onClick={() => deleteComment(p.id, c.id)} className="text-slate-300 hover:text-[#D90429] ml-auto" title="Delete comment">
                                              <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                          )}
                                        </div>
                                        <p className="text-sm text-slate-700 mt-0.5 whitespace-pre-wrap break-words">{c.text}</p>
                                        <button
                                          data-testid={`forum-like-comment-${c.id}`}
                                          onClick={() => toggleLikeComment(p.id, c.id)}
                                          className={`mt-1.5 inline-flex items-center gap-1.5 text-xs font-bold transition-colors duration-200 ${
                                            user && (c.liked_by || []).includes(user.email) ? "text-[#D90429]" : "text-slate-400 hover:text-[#D90429]"
                                          }`}
                                        >
                                          <ThumbsUp className="w-3.5 h-3.5" /> {c.likes || 0}
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
